/**
 * Formats a phone number string to (___) ___-____ format
 * @param value - The input value (digits only)
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(value: string): string {
	// Remove all non-digit characters
	const digits = value.replace(/\D/g, "");

	// Limit to 10 digits
	const limitedDigits = digits.slice(0, 10);

	// Format based on length
	if (limitedDigits.length === 0) return "";
	if (limitedDigits.length <= 3) return `(${limitedDigits}`;
	if (limitedDigits.length <= 6)
		return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;

	return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
}

/**
 * Extracts only digits from a formatted phone number
 * @param value - The formatted phone number string
 * @returns String containing only digits
 */
export function extractDigits(value: string): string {
	return value.replace(/\D/g, "");
}

/**
 * Digits persisted for a phone field. Caps at 10 to match the display mask.
 */
export function phoneValueForStorage(value: string): string {
	return extractDigits(value).slice(0, 10);
}

/**
 * Validates if a phone number has exactly 10 digits
 * @param value - The phone number string (formatted or unformatted)
 * @returns True if valid, false otherwise
 */
export function isValidPhoneNumber(value: string): boolean {
	const digits = extractDigits(value);
	return digits.length === 10;
}

/**
 * Handles phone number input changes with formatting
 * @param inputValue - The raw input value from the user
 * @returns Formatted phone number string
 */
export function handlePhoneInputChange(inputValue: string): string {
	// Remove all non-digit characters from input
	const digits = inputValue.replace(/\D/g, "");

	// Limit to 10 digits and format
	return formatPhoneNumber(digits);
}
